'use client';

import { c as _c } from "react/compiler-runtime";
import { Lightbox, useLightbox } from '~/features/lightbox';
import Carousel from '~/components/primitives/Carousel';
import { Icon } from '~/components/primitives';
import Image from 'next/image';
import React from 'react';
import Title from '~/components/utils/Title';
import { cx } from 'class-variance-authority';
import { useI18n } from '~/features/i18n';
import { getVideoThumbnails } from '~/utils/video';
const ACTIVITY_GALLERY_MEDIA_SIZES = '(max-width: 430px) 208px, (max-width: 750px) 300px, (max-width: 1080px) 350px, (max-width: 1200px) 400px, 450px';
const ACTIVITY_GALLERY_MEDIA_WIDTH = 400;
const ACTIVITY_GALLERY_MEDIA_HEIGHT = 256; // Math.round(ACTIVITY_GALLERY_MEDIA_WIDTH * (9 / 14));

type ActivityGallery_Slide_Data = {
  src: string;
  alt: string;
  id: number;
  type: string;
};
const ActivityPageGallery: React.FC<{
  slides: ActivityGallery_Slide_Data[];
  className?: string;
}> = props => {
  const $ = _c(31);
  const lightbox = useLightbox();
  const i18n = useI18n();
  if (!props.slides?.length) {
    return null;
  }
  let t0;
  if ($[0] !== props.className) {
    t0 = cx("relative", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== i18n) {
    t1 = i18n._("Photos");
    $[2] = i18n;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <Title as="h3" size="xl" weight="bold" className="mb-3">{t1}</Title>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== lightbox || $[7] !== props.slides) {
    let t4;
    if ($[9] !== lightbox) {
      t4 = (slide, idx) => <ActivityGallery_Slide key={slide.id} media={slide} onClick={() => lightbox.openIndex(idx)} />;
      $[9] = lightbox;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    t3 = props.slides.map(t4);
    $[6] = lightbox;
    $[7] = props.slides;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <div className="hidden w-full grid-cols-3 flex-wrap gap-4 sm:grid lg:grid-cols-2 xl:grid-cols-3">{t3}</div>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      container: "scroll-p-6 overflow-y-clip pl-0"
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== lightbox || $[15] !== props.slides) {
    let t7;
    if ($[17] !== lightbox) {
      t7 = (slide_0, idx_0) => <ActivityGallery_Slide key={slide_0.id} media={slide_0} onClick={() => lightbox.openIndex(idx_0)} />;
      $[17] = lightbox;
      $[18] = t7;
    } else {
      t7 = $[18];
    }
    t6 = props.slides.map(t7);
    $[14] = lightbox;
    $[15] = props.slides;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[19] !== t6) {
    t7 = <div className="relative left-1/2 -ml-[50vw] w-screen px-6 sm:hidden"><Carousel buttons={true} disableMarginLeft={true} classNames={t5} spacing="SM">{t6}</Carousel></div>;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== lightbox.close || $[22] !== lightbox.index || $[23] !== props.slides) {
    t8 = <Lightbox slides={props.slides} index={lightbox.index} className="py-10" onClose={lightbox.close} />;
    $[21] = lightbox.close;
    $[22] = lightbox.index;
    $[23] = props.slides;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t0 || $[26] !== t2 || $[27] !== t4 || $[28] !== t7 || $[29] !== t8) {
    t9 = <section className={t0}>{t2}{t4}{t7}{t8}</section>;
    $[25] = t0;
    $[26] = t2;
    $[27] = t4;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};
export default ActivityPageGallery;
const ActivityGallery_Slide: React.FC<ActivityGalleryItem_Props> = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = props.media.type.includes("video") ? <ActivityGallery_Slide_Video {...props} /> : <ActivityGallery_Slide_Image {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
type ActivityGalleryItem_Props = {
  media: ActivityGallery_Slide_Data;
  onClick(): void;
  className?: string;
};
const ActivityGallery_Slide_Image: React.FC<ActivityGalleryItem_Props> = props => {
  const $ = _c(10);
  let t0;
  if ($[0] !== props.className) {
    t0 = cx("aspect-14/9 relative flex w-52 overflow-hidden rounded-lg bg-white sm:w-auto", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = props.media.alt ?? "";
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      filter: "blur(0)"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.media.src || $[4] !== props.onClick || $[5] !== t1) {
    t3 = <Image src={props.media.src} alt={t1} width={ACTIVITY_GALLERY_MEDIA_WIDTH} height={ACTIVITY_GALLERY_MEDIA_HEIGHT} sizes={ACTIVITY_GALLERY_MEDIA_SIZES} className="scale-100 cursor-pointer rounded-2xl object-cover duration-150 ease-in hover:scale-105" onClick={props.onClick} style={t2} />;
    $[3] = props.media.src;
    $[4] = props.onClick;
    $[5] = t1;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t0 || $[8] !== t3) {
    t4 = <div data-component="ActivityGalleryImage" className={t0}>{t3}</div>;
    $[7] = t0;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const ActivityGallery_Slide_Video: React.FC<ActivityGalleryItem_Props> = props => {
  const $ = _c(13);
  const [thumbnail, setThumbnail] = React.useState(null);
  let t0;
  let t1;
  if ($[0] !== props.media.src) {
    t0 = () => {
      getVideoThumbnails(props.media.src).then(thumbnails => setThumbnail(thumbnails.small)).catch(error => {
        console.error("Failed to generate thumbnails:", error);
        setThumbnail(props.media.src);
      });
    };
    t1 = [props.media.src];
    $[0] = props.media.src;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  React.useEffect(t0, t1);
  let t2;
  if ($[3] !== props.className) {
    t2 = cx("aspect-14/9 group relative flex w-52 overflow-hidden rounded-lg sm:w-auto", props.className);
    $[3] = props.className;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== props.media.alt || $[6] !== props.onClick || $[7] !== thumbnail) {
    t3 = thumbnail && <Image className="scale-100 cursor-pointer rounded-2xl object-cover duration-150 ease-in group-hover:scale-105" src={thumbnail} alt={props.media.alt} onClick={props.onClick} width={ACTIVITY_GALLERY_MEDIA_WIDTH} height={ACTIVITY_GALLERY_MEDIA_HEIGHT} sizes={ACTIVITY_GALLERY_MEDIA_SIZES} />;
    $[5] = props.media.alt;
    $[6] = props.onClick;
    $[7] = thumbnail;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"><Icon name="play" size="xl" className="pointer-events-none text-white" /></div>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t2 || $[11] !== t3) {
    t5 = <div className={t2}>{t3}{t4}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};